import React from 'react';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { track } from 'core/helpers/analytics';
import { useFormikContext, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import * as Yup from 'yup';

import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import { appConfig } from 'brand-config';
import useSession from 'core/hooks/use-session';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { FormField } from 'core/components/form';
import { isValidVin } from 'core/helpers/vin-validator';
import Suggestions from 'common/components/people/suggestions/suggestions';
import LienHolderAutoComplete from 'common/components/people/additional-party/lien-holder-autocomplete';
import useStyles from 'common/components/people/add-form.styles';
import { useDisabledState } from 'common/disabled-context';
import { buildAddCarSubmitValues } from './helpers';

// Maps the shared LienHolderAutoComplete's generic fields onto the `lienholder`-prefixed
// Formik fields this form uses (prefixed to avoid colliding with the car fields in the same
// form). Without this, the selected/typed name lands in `name` instead of `lienholderName`,
// the required-field validation fails silently on submit, and "Add Car" does nothing.
const LIENHOLDER_FIELD_NAMES = {
  name: 'lienholderName',
  address: 'lienholderAddress',
  address2: 'lienholderAddress2',
  city: 'lienholderCity',
  state: 'lienholderState',
  zip: 'lienholderZip',
  loanNumber: 'lienholderLoanNumber'
};

const getInitialValues = (cars, inPolicyRoute) => {
  const firstCar = cars?.[0];
  const {
    deductibleCollision,
    deductibleComprehensive,
    limitRental,
    limitUMPD,
    limitUMBI,
    limitACPE,
    roadsideAssistance,
    extendedNonOwnedCoverage
  } = firstCar || {};

  const base = {
    deductibleCollision,
    deductibleComprehensive,
    limitRental,
    limitUMPD,
    limitUMBI,
    limitACPE,
    roadsideAssistance,
    extendedNonOwnedCoverage,
    carVIN: '',
    initialOpen: true,
    attestation: false,
    // A newly added CFM vehicle defaults to "solely owned" (true) — the common case — so the Joint
    // Owner select stays disabled/cleared unless the user unchecks it; jointOwner starts blank. Sole
    // ownership is per-vehicle, so it is NOT copied from the first car. Flag-gated.
    ...(appConfig?.featureFlags?.supportsVehicleOwnershipDetails
      ? { solelyOwnedByApplicant: true, jointOwner: '' }
      : {})
  };

  if (inPolicyRoute) {
    return {
      ...base,
      addLienholder: false,
      lienholderName: '',
      lienholderAddress: '',
      lienholderAddress2: '',
      lienholderCity: '',
      lienholderState: '',
      lienholderZip: '',
      lienholderLoanNumber: ''
    };
  }

  return base;
};

const findGarageLocation = (cars) => {
  if (!Array.isArray(cars) || !cars.length) {
    return undefined;
  }
  const [car] = cars;
  if (car) {
    return car.garageLocation;
  }
};

const handleSubmit = ({
  values,
  onAdd,
  cars,
  toast,
  isAgency,
  webUserId,
  policyId,
  parentSetFieldValue,
  existingAdditionalParties
}) => {
  const garageLocation = findGarageLocation(cars);
  const { car, additionalParty } = buildAddCarSubmitValues({ values, garageLocation });

  onAdd(car);

  // If lienholder data was provided, add it to the policy's additionalParties
  if (additionalParty && parentSetFieldValue) {
    const currentParties = existingAdditionalParties || [];
    parentSetFieldValue('additionalParties', [...currentParties, additionalParty]);
  }

  track('Car Added', {
    isAgency: isAgency,
    attestation: values.attestation,
    VIN: String(car.VIN).toUpperCase(),
    webUserId,
    policyId,
    hasLienholder: !!additionalParty
  });

  const firstCar = cars?.[0];
  toast.notify({
    type: 'success',
    message: `We've copied the coverages from this member's existing car, the ${firstCar.year} ${firstCar.make} ${firstCar.model}, onto this newly added one for ease of use. Feel free to make adjustments if needed.`
  });
};

const validationSchema = (cars) =>
  Yup.object().shape({
    carVIN: Yup.string()
      .required('VIN is required')
      .ensure()
      .test('is-valid-vin', 'Please enter a valid car VIN', (val) => isValidVin(val))
      .test('is-new-vin', 'This VIN is already included in your offer', (val) => !cars.find((car) => car.VIN === val))
  });

const attestationSchema = Yup.object().shape({
  attestation: Yup.boolean().test('attestation', 'Attestation is required', (value) => value)
});

const lienholderSchema = Yup.object().shape({
  addLienholder: Yup.boolean(),
  lienholderName: Yup.string().when('addLienholder', {
    is: true,
    then: (schema) => schema.required('Lienholder name is required when adding a lienholder'),
    otherwise: (schema) => schema
  }),
  lienholderAddress: Yup.string().when('addLienholder', {
    is: true,
    then: (schema) => schema.required('Lienholder address is required'),
    otherwise: (schema) => schema
  }),
  lienholderCity: Yup.string().when('addLienholder', {
    is: true,
    then: (schema) => schema.required('City is required'),
    otherwise: (schema) => schema
  }),
  lienholderState: Yup.string().when('addLienholder', {
    is: true,
    then: (schema) => schema.required('State is required'),
    otherwise: (schema) => schema
  }),
  lienholderZip: Yup.string().when('addLienholder', {
    is: true,
    then: (schema) => schema.required('Zip code is required'),
    otherwise: (schema) => schema
  })
});

const deriveAttestation = (drivers) => {
  const hasACoApplicant = drivers?.some((driver) => driver.isCoApplicant === true);
  const primaryDriver = drivers?.find((driver) => driver.isPrimary === true);

  if (hasACoApplicant) {
    const coApplicant = drivers?.find((driver) => driver.isCoApplicant === true);
    return `I have verbally confirmed that the vehicle is titled to ${primaryDriver.firstName} ${primaryDriver.lastName} and/or ${coApplicant.firstName} ${coApplicant.lastName}, that they receive no payment for its use, and that it has no pre-existing damage, including from accidents, storms, floods, or fires.`;
  }
  return `I have verbally confirmed that the vehicle is titled to ${primaryDriver.firstName} ${primaryDriver.lastName}, that they receive no payment for its use, and that it has no pre-existing damage, including from accidents, storms, floods, or fires.`;
};

function AddCar({ onAdd, id }) {
  const { account, offer } = useStore();
  const { inPolicyRoute } = useRouteHelper();
  const { viewOnly, isAgency } = useSession();
  const { disabled } = useDisabledState();
  const { values: parentValues, setFieldValue: parentSetFieldValue } = useFormikContext();
  const { [id]: cars } = parentValues || {};
  const initialValues = getInitialValues(cars, inPolicyRoute);
  const toast = useToast();
  const classes = useStyles();

  const schema = inPolicyRoute
    ? validationSchema(cars).concat(attestationSchema).concat(lienholderSchema)
    : validationSchema(cars);

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={schema}
      onSubmit={(values) =>
        handleSubmit({
          values,
          onAdd,
          cars,
          toast,
          isAgency,
          webUserId: offer?.offer?.webUserId,
          policyId: account?.policies?.policy?.policy?.id,
          parentSetFieldValue: inPolicyRoute ? parentSetFieldValue : null,
          existingAdditionalParties: parentValues?.additionalParties
        })
      }
    >
      {({ values: addCarValues, setValues }) => (
        <Form className={classes.container}>
          <Suggestions cars={cars} />
          {!inPolicyRoute ? (
            <Grid key="container" container justifyContent="space-between" alignItems="center" spacing={2}>
              <FormField id="carVIN" name="carVIN" type="string" label={`Car's VIN Number`} mode="light" xs={6} />
              <div>
                <Button
                  type="submit"
                  key="addButton"
                  mode="big"
                  variant="contained"
                  color="secondary"
                  className={classes.button}
                  disabled={viewOnly || disabled}
                >
                  Add Car
                </Button>
              </div>
            </Grid>
          ) : (
            <Grid key="container" container className={classes.attestContainer}>
              <FormField id="carVIN" name="carVIN" type="string" label={`Car's VIN Number`} mode="light" xs={6} />
              <FormField
                id="attestation"
                name="attestation"
                type="checkbox"
                label={deriveAttestation(account?.policies?.policy?.policyDetails?.drivers)}
                mode="light"
                xs={12}
              />
              <FormField
                id="addLienholder"
                name="addLienholder"
                type="checkbox"
                label="Add a lienholder for this vehicle"
                mode="light"
                xs={12}
              />
              {addCarValues.addLienholder && (
                <Grid container spacing={4} style={{ paddingLeft: 12, paddingRight: 12, marginTop: 4 }}>
                  <LienHolderAutoComplete
                    xs={12}
                    setValues={(newValues) => setValues({ ...addCarValues, ...newValues })}
                    currentValues={addCarValues}
                    fieldNames={LIENHOLDER_FIELD_NAMES}
                  />
                  <FormField
                    mode="light"
                    name="lienholderAddress"
                    id="lienholderAddress"
                    label="Lienholder Address"
                    type="string"
                    xs={8}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lienholderAddress2"
                    id="lienholderAddress2"
                    label="Address 2"
                    type="string"
                    optional
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lienholderCity"
                    id="lienholderCity"
                    label="City"
                    type="string"
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lienholderState"
                    id="lienholderState"
                    label="State"
                    type="state"
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lienholderZip"
                    id="lienholderZip"
                    label="Zip code"
                    type="string"
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lienholderLoanNumber"
                    id="lienholderLoanNumber"
                    label="Loan Number (optional)"
                    type="string"
                    optional
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                </Grid>
              )}
              <div className={classes.buttonRow}>
                <Button
                  type="submit"
                  key="addButton"
                  mode="big"
                  variant="contained"
                  color="secondary"
                  className={classes.submitButton}
                  disabled={viewOnly || disabled}
                >
                  Add Car
                </Button>
              </div>
            </Grid>
          )}
        </Form>
      )}
    </Formik>
  );
}

AddCar.propTypes = {
  onAdd: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired
};

export default observer(AddCar);
